/*
  rla-tile-loops.js: living tiles for sections/home-manifesto.liquid (Home Plan 260923, 5.3).

  A living tile is an ordinary photo tile whose still is the poster. The section renders this
  script only when at least one tile has a loop set, so a wall with no loops downloads nothing.

  THE CONTRACT
  - Poster first. The tile's <img> is the resting state and the only state under JS off,
    reduced motion, Save-Data, no IntersectionObserver, or a refused autoplay. No <video>
    element exists until the tile is within one viewport of the screen, and none at all in
    those fallback cases.
  - Plays only when at least half the tile is visible, and ONE plays at a time: the most
    visible eligible tile wins.
  - At most 5 seconds of play per entry into view (WCAG 2.2.2: the tile is a link, so a pause
    control inside it is not an option). After the budget the clip fades back to its poster
    and stays there until the tile has left the screen completely and comes back.
  - Pauses on tab hide and resumes (within the remaining budget) on return.
  - The clip sits absolutely inside the tile's fixed media box, so it contributes no layout
    shift, and it only fades in once it is actually playing.
  - Phones (max-width 639px) take data-rla-loop-src-phone when it is set (the lighter encode,
    plan cap 400 KB), otherwise the desktop source.

  Attributes (written by the section, never reused elsewhere; grep before adding one):
    data-rla-loop                 on the tile
    data-rla-loop-src             desktop mp4 url
    data-rla-loop-src-phone       optional phone mp4 url
*/
(function () {
  'use strict';

  var BUDGET_MS = 5000;
  var PLAY_RATIO = 0.5;
  var FADE_MS = 320;

  function reduced() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  function saveData() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return !!(c && c.saveData);
  }
  function isPhone() {
    return window.matchMedia && window.matchMedia('(max-width: 639px)').matches;
  }

  function Wall(root) {
    this.root = root;
    this.tiles = [];
    this.current = null;
    this.onVisibility = this.onVisibility.bind(this);
    this.init();
  }

  Wall.prototype.init = function () {
    var self = this;
    var nodes = this.root.querySelectorAll('[data-rla-loop]');
    if (!nodes.length) return;

    for (var i = 0; i < nodes.length; i++) {
      this.tiles.push({
        el: nodes[i],
        media: nodes[i].querySelector('.rla-manifesto__tile-media') || nodes[i],
        video: null,
        ratio: 0,
        spent: false,
        failed: false,
        playedMs: 0,
        startedAt: 0,
        timer: 0,
      });
    }

    // Approach observer: build the <video> (and so start the download) only within a viewport.
    this.near = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var t = self.find(e.target);
        if (t && !t.video && !t.failed) self.build(t);
        self.near.unobserve(e.target);
      });
    }, { rootMargin: '100% 0px 100% 0px', threshold: 0 });

    // Visibility observer: how much of each tile is on screen.
    this.seen = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var t = self.find(e.target);
        if (!t) return;
        t.ratio = e.isIntersecting ? e.intersectionRatio : 0;
        // A new entry into view restores the budget only after a complete exit.
        if (!e.isIntersecting) {
          t.spent = false;
          t.playedMs = 0;
        }
      });
      self.choose();
    }, { threshold: [0, 0.25, 0.5, 0.75, 1] });

    this.tiles.forEach(function (t) {
      self.near.observe(t.el);
      self.seen.observe(t.el);
    });
    document.addEventListener('visibilitychange', this.onVisibility);
  };

  Wall.prototype.find = function (el) {
    for (var i = 0; i < this.tiles.length; i++) if (this.tiles[i].el === el) return this.tiles[i];
    return null;
  };

  Wall.prototype.build = function (t) {
    var self = this;
    var src = (isPhone() && t.el.getAttribute('data-rla-loop-src-phone')) || t.el.getAttribute('data-rla-loop-src');
    if (!src) { t.failed = true; return; }
    var v = document.createElement('video');
    v.className = 'rla-manifesto__tile-loop';
    v.muted = true;
    v.defaultMuted = true;
    v.loop = true;
    v.playsInline = true;
    v.setAttribute('muted', '');
    v.setAttribute('playsinline', '');
    v.setAttribute('aria-hidden', 'true');
    v.setAttribute('tabindex', '-1');
    v.setAttribute('disablepictureinpicture', '');
    v.preload = 'auto';
    v.addEventListener('error', function () { self.fail(t); });
    v.src = src;
    t.media.appendChild(v);
    t.video = v;
    v.addEventListener('canplay', function () { self.choose(); }, { once: true });
  };

  Wall.prototype.fail = function (t) {
    t.failed = true;
    if (t.video) {
      t.video.classList.remove('is-playing');
      try { t.video.pause(); } catch (e) {}
    }
    if (this.current === t) this.current = null;
    this.choose();
  };

  Wall.prototype.eligible = function (t) {
    return t.video && !t.failed && !t.spent && t.ratio >= PLAY_RATIO && t.video.readyState >= 2;
  };

  Wall.prototype.choose = function () {
    if (document.hidden) return;
    var best = null;
    for (var i = 0; i < this.tiles.length; i++) {
      var t = this.tiles[i];
      if (!this.eligible(t)) continue;
      if (!best || t.ratio > best.ratio + 0.01) best = t;
    }
    // Keep the current clip while it is still eligible, unless another tile is clearly more visible.
    if (this.current && this.eligible(this.current) && best && best !== this.current && best.ratio <= this.current.ratio + 0.01) {
      best = this.current;
    }
    if (best === this.current) {
      if (best && best.video.paused) this.play(best);
      return;
    }
    if (this.current) this.stop(this.current, false);
    this.current = best;
    if (best) this.play(best);
  };

  Wall.prototype.play = function (t) {
    var self = this;
    var p;
    try { p = t.video.play(); } catch (e) { this.fail(t); return; }
    var started = function () {
      if (self.current !== t) { t.video.pause(); return; }
      t.video.classList.add('is-playing');
      t.startedAt = performance.now();
      clearTimeout(t.timer);
      t.timer = setTimeout(function () { self.expire(t); }, Math.max(0, BUDGET_MS - t.playedMs));
    };
    if (p && typeof p.then === 'function') {
      p.then(started, function (err) {
        // An AbortError is our own pause() interrupting a pending play(); the clip is fine.
        if (err && err.name === 'AbortError') return;
        self.fail(t);
      });
    } else {
      started();
    }
  };

  // Pause and bank the time played. `rest` fades back to the poster and rewinds.
  Wall.prototype.stop = function (t, rest) {
    clearTimeout(t.timer);
    if (t.startedAt) {
      t.playedMs += performance.now() - t.startedAt;
      t.startedAt = 0;
    }
    if (!t.video) return;
    try { t.video.pause(); } catch (e) {}
    t.video.classList.remove('is-playing');
    if (rest) {
      var v = t.video;
      setTimeout(function () { if (!v.classList.contains('is-playing')) { try { v.currentTime = 0; } catch (e) {} } }, FADE_MS);
    }
  };

  Wall.prototype.expire = function (t) {
    t.spent = true;
    this.stop(t, true);
    if (this.current === t) this.current = null;
    this.choose();
  };

  Wall.prototype.onVisibility = function () {
    if (document.hidden) {
      if (this.current) this.stop(this.current, false);
    } else {
      this.choose();
    }
  };

  Wall.prototype.destroy = function () {
    var self = this;
    if (this.near) this.near.disconnect();
    if (this.seen) this.seen.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.tiles.forEach(function (t) {
      self.stop(t, false);
      if (t.video && t.video.parentNode) t.video.parentNode.removeChild(t.video);
    });
    this.tiles = [];
  };

  var walls = new Map();

  function mount(scope) {
    if (reduced() || saveData() || !('IntersectionObserver' in window)) return;
    var roots = (scope || document).querySelectorAll('.rla-manifesto');
    for (var i = 0; i < roots.length; i++) {
      if (walls.has(roots[i])) continue;
      if (!roots[i].querySelector('[data-rla-loop]')) continue;
      walls.set(roots[i], new Wall(roots[i]));
    }
  }

  function unmount(scope) {
    walls.forEach(function (w, root) {
      if (scope.contains(root)) {
        w.destroy();
        walls.delete(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { mount(document); });
  } else {
    mount(document);
  }
  document.addEventListener('shopify:section:load', function (e) { mount(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { unmount(e.target); });
})();
